import { CloseCircleFilled, DownOutlined, ToolOutlined } from '@ant-design/icons';
import { ServicePickerDrawer } from '@components';
import { L } from '@lib';
import { Service } from '@services';
import { useStore } from '@stores';
import { Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import React, { useEffect, useMemo, useState } from 'react';

const { Text } = Typography;

interface IProps {
  cleanName?: boolean;
  showMyServicesOnly?: boolean;
  placeholder?: string;
  mode?: 'multiple' | 'tags';
  status?: 'error' | 'warning';
  stickyHeader?: boolean;
  value?: number | number[];
  allowClear?: boolean;
  disabled?: boolean;
  fixedOnly?: boolean;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  defaultValue?: { label: string | undefined; value: number | undefined }[];
  onClear?: () => void;
  onSelect?: (id: number) => void;
  onDeselect?: (id: number) => void;
}

const triggerBaseStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: 32,
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  padding: '2px 28px 2px 10px',
  background: '#fff',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 4,
  cursor: 'pointer',
  position: 'relative',
  transition: 'border-color 0.15s',
};

const statusBorder = (status?: 'error' | 'warning') => {
  if (status === 'error') return '#ff4d4f';
  if (status === 'warning') return '#faad14';
  return undefined;
};

export const ServiceSelect: React.FC<IProps> = observer(
  ({
    mode,
    fixedOnly = false,
    cleanName = false,
    placeholder = L('Service select'),
    showMyServicesOnly = false,
    status,
    value,
    allowClear = true,
    defaultValue,
    disabled = false,
    onClear,
    onSelect,
    onDeselect,
  }) => {
    const { serviceStore } = useStore();
    const isMulti = mode === 'multiple' || mode === 'tags';

    const [open, setOpen] = useState(false);
    const [labelMap, setLabelMap] = useState<Record<number, string>>({});
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
      if (!defaultValue) return;
      setLabelMap((prev) => {
        const next = { ...prev };
        defaultValue.forEach((d) => {
          if (d.value != null && d.label && next[d.value] === undefined) {
            next[d.value] = d.label;
          }
        });
        return next;
      });
    }, [defaultValue]);

    const selectedIds = useMemo<number[]>(() => {
      if (value == null) return [];
      return Array.isArray(value) ? value.filter((v) => v != null) : [value];
    }, [value]);

    const getLabel = (id: number) => labelMap[id] ?? `#${id}`;

    const handleSelect = (service: Service) => {
      const label = (cleanName ? service.name : service.normalizedName) ?? service.name ?? '';
      setLabelMap((prev) => ({ ...prev, [service.id]: label }));
      runInAction(() => {
        serviceStore.selectServices.set(service.id, service);
      });
      onSelect?.(service.id);
    };

    const handleDeselect = (id: number) => {
      onDeselect?.(id);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear?.();
    };

    const openDrawer = () => {
      if (disabled) return;
      setOpen(true);
    };

    const borderColor = statusBorder(status);
    const triggerStyle: React.CSSProperties = {
      ...triggerBaseStyle,
      borderColor: borderColor ?? (hovered && !disabled ? '#4096ff' : '#d9d9d9'),
      background: disabled ? '#f5f5f5' : '#fff',
      cursor: disabled ? 'not-allowed' : 'pointer',
    };

    const showClear = allowClear && !disabled && selectedIds.length > 0;

    return (
      <>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={openDrawer}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openDrawer();
            }
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={triggerStyle}
        >
          {selectedIds.length === 0 && (
            <Text type="secondary" style={{ flex: 1, color: '#bfbfbf', fontSize: 14 }}>
              {placeholder}
            </Text>
          )}

          {selectedIds.length > 0 && !isMulti && (
            <Text style={{ flex: 1, fontSize: 14 }} ellipsis>
              {getLabel(selectedIds[0])}
            </Text>
          )}

          {selectedIds.length > 0 &&
            isMulti &&
            selectedIds.map((id) => (
              <Tag
                key={id}
                icon={<ToolOutlined />}
                closable={!disabled}
                onClose={(e) => {
                  e.preventDefault();
                  handleDeselect(id);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  margin: '2px 0',
                  padding: '2px 8px',
                  background: '#f5f5f5',
                  border: '1px solid #bfbfbf',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#333',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {getLabel(id)}
              </Tag>
            ))}

          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#bfbfbf',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {showClear && hovered ? (
              <CloseCircleFilled
                onClick={handleClear}
                style={{ cursor: 'pointer', color: '#bfbfbf' }}
              />
            ) : (
              <DownOutlined />
            )}
          </span>
        </div>

        <ServicePickerDrawer
          open={open}
          mode={isMulti ? 'multiple' : 'single'}
          selectedIds={selectedIds}
          selectedLabels={labelMap}
          cleanName={cleanName}
          fixedOnly={fixedOnly}
          showMyServicesOnly={showMyServicesOnly}
          title={placeholder}
          onClose={() => setOpen(false)}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          onClear={allowClear ? onClear : undefined}
        />
      </>
    );
  }
);
